import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * GLOBAL deduplication map — shared across ALL hook instances and re-renders.
 * Uses msg_id from server (unique per broadcast) to guarantee 1 toast per event.
 */
const _globalDedup = new Map();

function isGlobalDuplicate(data) {
  const key = data.msg_id || `${data.type}_${data.work_order_id || data.wo_id || ''}_${data.timestamp || ''}`;
  const now = Date.now();
  const last = _globalDedup.get(key);
  if (last) return true; // Already seen this exact message
  _globalDedup.set(key, now);
  // Cleanup old entries
  for (const [k, t] of _globalDedup) {
    if (now - t > 60000) _globalDedup.delete(k);
  }
  return false;
}

export function useVehicleReadyNotification() {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  const playNotificationSound = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, startTime, duration, vol = 0.8) => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.type = 'square';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(vol, audioCtx.currentTime + startTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + startTime + duration);
        osc.start(audioCtx.currentTime + startTime);
        osc.stop(audioCtx.currentTime + startTime + duration);
      };
      for (let r = 0; r < 3; r++) {
        const offset = r * 0.9;
        playTone(800, offset + 0, 0.12, 0.9);
        playTone(1000, offset + 0.1, 0.12, 0.9);
        playTone(1200, offset + 0.2, 0.12, 0.9);
        playTone(1400, offset + 0.3, 0.25, 0.9);
      }
    } catch (e) {
      console.warn('Audio notification failed:', e);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let ws = null;
    let reconnectTimer = null;

    const connect = () => {
      if (!mountedRef.current) return;
      // Close existing
      if (ws) {
        try { ws.close(); } catch { /* */ }
        ws = null;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/ws/notifications`;

      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          if (!mountedRef.current) return;
          try {
            const data = JSON.parse(event.data);
            // GLOBAL dedup — prevents duplicates across all connections
            if (isGlobalDuplicate(data)) return;

            if (data.type === 'vehicle_ready') {
              playNotificationSound();
              toast.success(
                `VEHICULE PRET`,
                {
                  description: `${data.vehicle_info || 'Vehicule'} — ${data.client_name || 'Client'}\nMarque par ${data.mechanic_name || 'Atelier'}`,
                  duration: 30000,
                  important: true,
                  style: { background: '#ecfdf5', border: '2px solid #10b981', fontSize: '14px' },
                }
              );
            } else if (data.type === 'atelier_parts_order') {
              playNotificationSound();
              toast.warning(
                `COMMANDE PIECE — ATELIER`,
                {
                  description: `${data.mechanic_name} a commande chez ${data.supplier}\nN° commande: ${data.order_number}\nFiche: ${data.wo_reference} — ${data.vehicle_info || ''}\n${data.client_name || ''}`,
                  duration: 60000,
                  important: true,
                  style: { background: '#fffbeb', border: '2px solid #f59e0b', fontSize: '14px' },
                }
              );
            }
          } catch (e) {
            console.warn('WebSocket message parse error:', e);
          }
        };

        ws.onclose = () => {
          if (mountedRef.current) {
            reconnectTimer = setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (e) {
        console.warn('WebSocket connection error:', e);
        if (mountedRef.current) {
          reconnectTimer = setTimeout(connect, 5000);
        }
      }
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        try { ws.close(); } catch { /* */ }
      }
      wsRef.current = null;
    };
  }, [playNotificationSound]);
}
